import React from 'react';

/** Presets por defecto si el venue no devuelve lista personalizada. */
export const DEFAULT_MEMBERSHIP_DURATIONS = [
  { id: '__1m', label: '1 Mes', months: 1 },
  { id: '__3m', label: '3 Meses', months: 3 },
  { id: '__1y', label: '1 Año', months: 12 },
];

export function addMonths(date, n) {
  const d = new Date(date);
  d.setMonth(d.getMonth() + n);
  return d;
}

/** Preset cuyo cálculo inscripción + N meses coincide con la caducidad (misma lógica que los botones). */
export function findMatchingDurationPresetId(enrollmentIso, expirationIso, durationList) {
  if (!enrollmentIso || !expirationIso || !durationList?.length) return null;
  const [y, m, d] = enrollmentIso.split('-').map(Number);
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  const enr = new Date(y, m - 1, d);
  for (const preset of durationList) {
    const months = Number(preset.months);
    if (!Number.isFinite(months) || months <= 0) continue;
    const end = addMonths(new Date(enr), months);
    const iso = `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`;
    if (iso === expirationIso) return preset.id;
  }
  return null;
}

/** enrollment_date / expiration_date pueden venir en `profile` o en la raíz (listados / merges). */
export function getProfileDateRaw(member, field) {
  if (!member) return undefined;
  const fromProfile = member.profile?.[field];
  if (fromProfile != null && fromProfile !== '') return fromProfile;
  return member[field];
}

/** Valor para radios Lúd./Terap.: API puede enviar `consumption_type` o columnas planas. */
export function getConsumptionTypeValue(member) {
  if (!member) return '';
  const p = member.profile ?? member;
  const ct = p?.consumption_type;
  if (typeof ct === 'string') {
    const s = ct.trim().toLowerCase();
    if (s === 'ludenos' || s === 'lúd' || s === 'lud') return 'ludenos';
    if (s === 'terap' || s === 'terapéutico' || s === 'terapeutico') return 'terap';
  }
  if (ct && typeof ct === 'object') {
    if (ct.ludenos) return 'ludenos';
    if (ct.terap) return 'terap';
    return '';
  }
  if (p?.consumption_ludenos) return 'ludenos';
  if (p?.consumption_terap) return 'terap';
  return '';
}

/**
 * Convierte fecha de API a texto dd/mm/aaaa e ISO yyyy-mm-dd.
 * Acepta string ISO, solo fecha, u objeto Date.
 */
export function parseDayFromApi(raw) {
  if (raw == null || raw === '') return { display: '', iso: '' };
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return { display: toDisplay(d), iso: toISO(d) };
  }
  if (typeof raw === 'string') {
    const s = raw.split('T')[0];
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split('-');
      return { display: `${d}/${m}/${y}`, iso: s };
    }
  }
  if (raw instanceof Date && !Number.isNaN(raw.getTime())) {
    return { display: toDisplay(raw), iso: toISO(raw) };
  }
  return { display: '', iso: '' };
}

export function toISO(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function toDisplay(date) {
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

/** Igual que `formatDate` en CustomerModal: texto dd/mm/aaaa o ISO → yyyy-mm-dd; vacío → 2000-01-01. */
export function formatDateForApi(s) {
  if (!s || s.trim() === '') return '2000-01-01';
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (s.includes('/')) {
    const [d, m, y] = s.split('/');
    return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
  }
  const dt = new Date(s);
  if (!Number.isNaN(dt.getTime())) return dt.toISOString().split('T')[0];
  return '2000-01-01';
}

/** Texto + calendario (mismo patrón que editar/crear socio). */
export function DateField({ label, value, onChange, pickerValue, onPickerChange, placeholder = 'dd/mm/aaaa' }) {
  return (
    <div className="wa-form-field">
      <label className="wa-label">{label}</label>
      <div className="relative">
        <input
          type="text"
          value={value}
          onChange={(e) => {
            let v = e.target.value.replace(/[^\d/]/g, '');
            if (v.length <= 10) {
              const nums = v.replace(/\//g, '');
              let fmt = '';
              for (let i = 0; i < nums.length; i += 1) {
                if (i === 2 || i === 4) fmt += '/';
                fmt += nums[i];
              }
              onChange(fmt);
              if (fmt.length === 10 && /^\d{2}\/\d{2}\/\d{4}$/.test(fmt)) {
                const [d, m, y] = fmt.split('/');
                onPickerChange(`${y}-${m}-${d}`);
              }
            }
          }}
          placeholder={placeholder}
          maxLength={10}
          className="wa-input pr-10"
        />
        <input
          type="date"
          value={pickerValue}
          onChange={(e) => {
            if (e.target.value) {
              const [y, m, d] = e.target.value.split('-');
              onChange(`${d}/${m}/${y}`);
              onPickerChange(e.target.value);
            }
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 opacity-0 cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-base" aria-hidden>
          📅
        </span>
      </div>
    </div>
  );
}

/** Botones 1m / 3m / 1a + resumen + inscripción y caducidad. */
export function MembershipDurationEditor({
  durations,
  selectedDurationId,
  setSelectedDurationId,
  setInscripcionInput,
  setDatePickerInscripcion,
  setCaducidadInput,
  setDatePickerCaducidad,
  inscripcionInput,
  datePickerInscripcion,
  caducidadInput,
  datePickerCaducidad,
}) {
  return (
    <div className="space-y-3">
      <div className="wa-form-field">
        <label className="wa-label">Duración de inscripción</label>
        <div className="flex flex-wrap gap-2">
          {durations.map((d) => {
            const isActive = selectedDurationId === d.id;
            return (
              <button
                key={d.id}
                type="button"
                onClick={() => {
                  const now = new Date();
                  const expiry = addMonths(now, d.months);
                  setSelectedDurationId(d.id);
                  setInscripcionInput(toDisplay(now));
                  setDatePickerInscripcion(toISO(now));
                  setCaducidadInput(toDisplay(expiry));
                  setDatePickerCaducidad(toISO(expiry));
                }}
                className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                  isActive
                    ? 'bg-lime-500 text-white border-lime-500 shadow-sm'
                    : 'bg-white text-gray-700 border-gray-300 hover:border-lime-400 hover:text-lime-700'
                }`}
              >
                {d.label}
              </button>
            );
          })}
        </div>
        {selectedDurationId && inscripcionInput && (
          <p className="mt-1.5 text-xs text-gray-500">
            Inscripción: <span className="font-medium text-gray-700">{inscripcionInput}</span>
            {' · '}
            Caducidad: <span className="font-medium text-gray-700">{caducidadInput}</span>
          </p>
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <DateField
          label="Inscripción"
          value={inscripcionInput}
          onChange={setInscripcionInput}
          pickerValue={datePickerInscripcion}
          onPickerChange={setDatePickerInscripcion}
        />
        <DateField
          label="Caducidad"
          value={caducidadInput}
          onChange={setCaducidadInput}
          pickerValue={datePickerCaducidad}
          onPickerChange={setDatePickerCaducidad}
        />
      </div>
    </div>
  );
}
